import { useCognitoAuth } from 'features/auth';
import {
  FullscreenLoading,
  InputSelect,
  SiteHeader,
  SiteHeaderNavLink,
  useBreakpoint,
  useModal,
  useMuiSwitchableTheme,
  useToastr,
} from '@farmshare/ui-components';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Announcement,
  AutoGraph,
  CalendarMonth,
  CalendarMonthOutlined,
  ChatBubbleOutline,
  ChecklistOutlined,
  GroupOutlined,
  LockOutline,
  PermIdentity,
  Person,
  Search,
  SettingsOutlined,
  StickyNote2Rounded,
  WorkOutline,
} from '@mui/icons-material';
import { Badge, Box, IconButton, Stack } from '@mui/material';
import { environment } from 'environments/environment';
import { debounce, find, first, map, some, sortBy } from 'lodash';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import moment from 'moment';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { getFeedback, setUser as sentrySetUser } from '@sentry/react';
import { ServiceWorkerProvider } from 'components/service-worker-provider';
import { isRunningInIframe } from 'utils/iframe-utils';
import Intercom, {
  onUnreadCountChange as intercomOnUnreadCountChange,
  showMessages as intercomShowMessagingComponent,
  showSpace as intercomShowSpace,
  update as intercomUpdateUserDetails,
  whoami as intercomWhoami,
} from '@intercom/messenger-js-sdk';

import {
  EnumProcessorSchedulingAnimalSpecies,
  EnumUserRole,
  useCalculateProcessorCapacityAllSpeciesLazyQuery,
  useCreateProcessorSchedulingMutation,
  type User,
  useUserTenantVendorLazyQuery,
  useUserUpdateActiveVendorMutation,
  type Vendor,
} from 'lib/graphql';
import { useAuthStorageCleanup } from 'lib/hooks';
import { ModuleId, useActiveModules } from 'lib/modules';

import logo from './assets/images/logo-roca-green.svg';
import whiteLogo from './assets/images/logo-roca-white.svg';
import { userState, vendorState } from './state';

import type { AuthProps } from '@farmshare/ui-components/src/components/auth/auth';
import { ALL_SHOWN_STATUSES } from 'pages/processor/lib/helpers/processorStatusPresentation';
import { isPlaywrightTestMode } from 'lib/test-mode';
import { isProcessor, isProducer } from 'lib/userHelpers';
import { AdminUserSearch } from 'components/admin-user-search';
import { ProducerShell } from 'features/producer-buyer-portal';

const SearchDrawer = lazy(() =>
  import('./pages/processor/calendar/_views/mobile').then((module) => ({
    default: module.SearchDrawer,
  })),
);

const FabMenu = lazy(() =>
  import('./pages/processor/calendar/_views/mobile').then((module) => ({
    default: module.FabMenu,
  })),
);

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
    LOQ: any[];
    luckyOrangeCompletedSetup: boolean;
  }
}

Intercom({
  app_id: 'n0tvm2gp',
});

function tagUserForGTM(user?: User) {
  if (!user || !window.dataLayer) {
    return;
  }

  window.dataLayer.push({
    event: 'user',
    userId: user._id,
    email: user.email,
    name: `${user.first_name} ${user.last_name}`,
    vendorId: user.active_vendor?._id,
    vendorName: user.active_vendor?.shop_name,
    role: user.role,
  });
}

function tagUserForSentry(user: User | null = null) {
  sentrySetUser(
    user
      ? {
          id: user._id,
          email: `${user.email}`,
          name: `${user.first_name} ${user.last_name}`,
          vendorId: user.active_vendor?._id,
          vendorName: user.active_vendor?.shop_name,
          role: user.role,
        }
      : null,
  );
}

function tagUserForIntercom(user: User | null = null) {
  if (!user) return intercomUpdateUserDetails({});

  return intercomUpdateUserDetails({
    api_base: 'https://api-iam.intercom.io',
    intercom_user_jwt: user.intercomWebToken,
    user_id: user._id,
    email: user.email || undefined,
    name:
      `${user.first_name || ''} ${user.last_name || ''}`.trim() || undefined,
    Role: user.role,
    company: user.active_vendor
      ? {
          company_id: user.active_vendor._id,
          name: user.active_vendor.shop_name,
        }
      : undefined,
  });
}

let didSetupLuckyOrange = false;

function setupLuckyOrange(localUser?: User) {
  if (!didSetupLuckyOrange && localUser && import.meta.env.PROD) {
    didSetupLuckyOrange = true;
    window.LOQ = window.LOQ || [];
    window.luckyOrangeCompletedSetup = true;

    window.LOQ.push([
      'ready',
      async (LO: any) => {
        await LO.$internal.ready('visitor');
        LO.visitor.identify(localUser._id, {
          email: `${localUser.email}`,
          phone: `${localUser.phone}`,
          name: `${localUser.first_name} ${localUser.last_name}`,
          role: localUser.role,
        });
      },
    ]);
  }
}

export function App() {
  const isTestMode = isPlaywrightTestMode();

  const {
    isAuthenticated,
    isLoading,
    logout,
    setImpersonateUserId,
    clearImpersonation,
    impersonateUserId,
  } = useCognitoAuth();

  const { save, multiStep } = useModal();
  const { push: pushToast } = useToastr();

  const { marginLeft, marginTop, themeMode } = useMuiSwitchableTheme();
  const breakpoint = useBreakpoint();
  const [user, setUserState] = useRecoilState(userState);
  const [vendor, setVendor] = useRecoilState(vendorState);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]);

  const activeModules = useActiveModules();

  const [ready, setReady] = useState(false);
  const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false);
  const [createScheduling] = useCreateProcessorSchedulingMutation();
  const [calculateProcessorCapacityAllSpeciesQuery] =
    useCalculateProcessorCapacityAllSpeciesLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const [capacityData, setCapacityData] = useState<any>(null);
  const { cleanupApolloCache, markLogout } = useAuthStorageCleanup();

  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const isMobileView = ['xs', 'sm', 'md'].includes(breakpoint);
  const isProcessorPage = pathname.startsWith('/processor');
  const [alerts, setAlerts] = useState<Record<string, number>>({});

  const handleSearchViewOnCalendar = (date: string) => {
    setIsSearchDrawerOpen(false);
    navigate(`/processor/calendar?selectedDate=${date}`);
  };

  intercomOnUnreadCountChange((unreadCount: number) => {
    if (unreadCount === alerts.support) {
      return;
    }
    setAlerts((prevAlerts) => ({
      ...prevAlerts,
      support: unreadCount,
    }));
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  const [search] = useSearchParams();

  const [getUser, getUserOperation] = useUserTenantVendorLazyQuery({
    notifyOnNetworkStatusChange: false,
  });
  const [updateUserActiveVendor] = useUserUpdateActiveVendorMutation();

  const handleAddJob = useCallback(async () => {
    // Load capacity data before opening modal
    const startDate = moment().format('YYYY-MM-DD');
    const endDate = moment().add(3, 'months').format('YYYY-MM-DD');
    const result = await calculateProcessorCapacityAllSpeciesQuery({
      variables: {
        vendorId: vendor?._id || '',
        startDate,
        endDate,
        dateFormat: 'M/DD/YYYY',
        groupFormat: 'MM/YYYY',
      },
    });
    setCapacityData(result.data);

    const { createQuickScheduleMultiStepConfig } = await import(
      'features/jobs/components/quick-schedule-steps'
    );

    multiStep((onClose: () => void) => {
      const config = createQuickScheduleMultiStepConfig({
        vendorId: vendor?._id || '',
        initialValues: {},
        createScheduling,
        capacityData,
        pushToast,
        onSuccess: () => {
          // Navigate to the job details page after creation
          // The scheduleId will be available in the success handler
        },
      });
      return config;
    });
  }, [
    calculateProcessorCapacityAllSpeciesQuery,
    capacityData,
    createScheduling,
    multiStep,
    pushToast,
    vendor?._id,
  ]);

  // Redirect to Cognito login
  const handleLogin = async (): Promise<void> => {
    navigate('/login');
  };

  useEffect(() => {
    if (isAuthenticated) {
      getUser();
    }
    setReady(true);
  }, [getUser, isAuthenticated]);

  useEffect(() => {
    if (getUserOperation.data?.userTenantVendor) {
      let localVendor: Vendor;
      const localUser = getUserOperation.data.userTenantVendor as User;

      if (getUserOperation.data.userTenantVendor.active_vendor) {
        localVendor = getUserOperation.data.userTenantVendor
          .active_vendor as Vendor;
      } else {
        localVendor = first(
          getUserOperation.data.userTenantVendor.tenant?.vendors,
        ) as Vendor;
      }

      if (localUser && localUser._id !== user?._id) {
        setUserState(localUser);
      }

      if (localVendor && localVendor._id !== vendor?._id) {
        setVendor(localVendor as Vendor);
      }
    }
  }, [
    getUserOperation.data?.userTenantVendor,
    setUserState,
    setVendor,
    vendor,
    user,
  ]);

  // Check if the user is loaded and matches the current user state
  // This is to ensure that the user data is fully loaded before proceeding
  const isUserLoaded = useMemo(() => {
    // If not authenticated, skip user loading check
    if (!isAuthenticated) {
      return true;
    }

    if (!ready || getUserOperation.loading) {
      return false;
    }
    return user?._id === getUserOperation.data?.userTenantVendor?._id;
  }, [
    ready,
    user,
    getUserOperation.loading,
    getUserOperation.data?.userTenantVendor,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (search.has('error')) {
      // eslint-disable-next-line no-console
      console.error('[Auth Error]', search.get('error_description'));

      markLogout();

      // Skip Auth0 logout in test mode — there's no real session to end.
      if (!isTestMode) {
        logout();
      }
    }
  }, [logout, search, markLogout, isTestMode]);

  /**
   * Filter nav links based on module and role requirements
   * @param links
   */
  const filterNavLinks = (links: SiteHeaderNavLink[]): SiteHeaderNavLink[] =>
    links
      .filter((link) => {
        // Check module requirement
        if (
          link.requiredModule &&
          !activeModules.some(
            (module) => module.config.id === link.requiredModule,
          )
        ) {
          return false;
        }

        const role = user?.role ?? 'guest';

        // Check role requirement
        if (
          link.requiredRoles &&
          !link.requiredRoles.includes(role as string)
        ) {
          return false;
        }
        return true;
      })
      .map((link) => {
        // Filter dropdown items
        if (link.dropDownItems) {
          return {
            ...link,
            dropDownItems: link.dropDownItems.filter((item) => {
              if (
                item.requiredModule &&
                !activeModules.some(
                  (module) => module.config.id === item.requiredModule,
                )
              ) {
                return false;
              }
              if (
                item.requiredRoles &&
                user &&
                !item.requiredRoles.includes(user.role as string)
              ) {
                return false;
              }
              return true;
            }),
          };
        }
        return link;
      });

  const buildNavLinks = (): SiteHeaderNavLink[] => {
    const processorRoles = [
      EnumUserRole.Vendor,
      EnumUserRole.VendorOwner,
      EnumUserRole.Admin,
    ];

    return [
      // Non-processor
      {
        label: 'Dashboard',
        href: '/dashboard',
        icon: <ChecklistOutlined />,
        requiredRoles: [EnumUserRole.Basic],
      },
      {
        label: 'Chat',
        href: '/chat',
        icon: <ChatBubbleOutline />,
        requiredModule: ModuleId.CHAT,
        requiredRoles: [EnumUserRole.Basic],
      },
      {
        label: 'Scheduling',
        href: '/scheduling',
        icon: <CalendarMonthOutlined />,
        requiredRoles: [EnumUserRole.Basic, 'guest'],
      },
      // Processor
      {
        label: 'Dashboard',
        href: '/processor/dashboard',
        icon: <ChecklistOutlined />,
        requiredRoles: processorRoles,
      },
      {
        label: 'Reporting',
        icon: <AutoGraph />,
        href: '/processor/reporting',
        requiredModule: ModuleId.REPORTING,
        requiredRoles: [EnumUserRole.Vendor],
      },
      {
        label: 'Reporting',
        icon: <AutoGraph />,
        requiredModule: ModuleId.REPORTING,
        requiredRoles: [EnumUserRole.VendorOwner, EnumUserRole.Admin],
        dropDownItems: [
          { label: 'Operations', href: '/processor/reporting' },
          { label: 'Revenue', href: '/processor/owner' },
          { label: 'Forecasting', href: '/processor/forecasting' },
          { label: 'Settings', href: '/processor/reporting-config' },
        ],
      },
      {
        label: 'Calendar',
        href: '/processor/calendar',
        icon: <CalendarMonth />,
        requiredRoles: processorRoles,
      },
      {
        label: 'Customers',
        icon: <GroupOutlined />,
        requiredRoles: processorRoles,
        dropDownItems: [
          { label: 'All Customers', href: '/processor/customers' },
          {
            label: 'Chat',
            href: '/processor/chat',
            requiredModule: ModuleId.CHAT,
          },
          { label: 'Waitlist', href: '/processor/settings/waitlist' },
        ],
      },
      {
        label: 'Jobs',
        icon: <WorkOutline />,
        requiredRoles: processorRoles,
        dropDownItems: [
          {
            label: 'All Jobs',
            href: '/processor/jobs',
            requiredRoles: processorRoles,
          },
          {
            label: 'Add Job',
            onClick: handleAddJob,
            requiredRoles: processorRoles,
          },
          {
            label: 'Invoices',
            href: '/processor/invoices',
            requiredRoles: processorRoles,
            requiredModule: ModuleId.INVOICES,
          },
        ],
      },
      {
        label: 'Cut Sheets',
        icon: (
          <Badge badgeContent="Beta" color="secondary">
            <StickyNote2Rounded />
          </Badge>
        ),
        requiredModule: ModuleId.CAPABILITIES_V2,
        requiredRoles: processorRoles,
        dropDownItems: [
          { label: 'Cut Sheet Builder', href: '/processor/cutsheets-v2' },
          {
            label: 'Cutsheet Library',
            href: '/processor/questionnaire-responses',
          },
        ],
      },
      {
        label: 'Settings',
        icon: <SettingsOutlined />,
        requiredRoles: processorRoles,
        dropDownItems: [
          { label: 'Processor Settings', href: '/processor/settings' },
          {
            label: 'Capabilities (Legacy)',
            href: '/processor/capabilities',
            requiredModule: ModuleId.CAPABILITIES_V1,
          },
          {
            label: 'Cutsheets (Legacy)',
            href: '/processor/cutsheets',
            requiredModule: ModuleId.CAPABILITIES_V1,
          },

          {
            label: 'Integrations',
            href: '/integrations',
            requiredModule: ModuleId.INTEGRATIONS,
          },
          {
            label: 'Exports',
            href: '/exports',
            requiredModule: ModuleId.EXPORTS,
          },
          {
            label: 'Notifications',
            href: '/processor/notifications',
            requiredModule: ModuleId.NOTIFICATIONS,
          },
        ],
      },
      {
        label: 'Admin',
        icon: <LockOutline />,
        href: '/admin',
        requiredRoles: [EnumUserRole.Admin],
      },
      {
        label: 'Support & News',
        icon: <Announcement />,
        dropDownItems: [
          {
            label: "What's New",
            onClick: () => intercomShowSpace('news'),
          },
          {
            label: 'Chat with Us',
            onClick: () => intercomShowMessagingComponent(),
            alerts: alerts.support,
          },
        ],
      },
    ];
  };

  const dashboardNavLinks = useMemo(
    () => filterNavLinks(buildNavLinks()),
    [user?.role, alerts.support, handleAddJob, activeModules],
  );

  if (
    allVendors.length === 0 &&
    (getUserOperation.data?.userTenantVendor?.tenant?.vendors?.length || 0) > 1
  ) {
    setAllVendors(
      getUserOperation.data?.userTenantVendor?.tenant?.vendors as Vendor[],
    );
  }

  const dropdownItems = useMemo(() => {
    const items: AuthProps['dropdownItems'] = [
      { label: 'Profile', icon: <Person />, href: '/profile' },
    ];

    if ((allVendors.length || 0) > 1) {
      items.push({
        label: 'Switch Vendor',
        icon: <FontAwesomeIcon icon={faRepeat} className="me-2" />,
        onClick: () => {
          save<{ vendor: string }>({
            type: 'save',
            title: 'Switch Vendor',
            icon: faRepeat,
            initialValues: { vendor: vendor?._id },
            saveIcon: faRepeat,
            saveText: 'Switch',
            body: (
              <InputSelect
                label="Vendor"
                options={map(
                  sortBy(allVendors, (v) => v.shop_name),
                  (v) => ({ label: v.shop_name, value: v._id }),
                )}
                floatingLabel
                required
              />
            ),
            onSubmit: (values) => {
              const vendor = find(allVendors, (v) => v._id === values.vendor);

              if (vendor) {
                updateUserActiveVendor({
                  variables: { userId: user._id, active_vendor: values.vendor },
                }).then(() => {
                  setVendor(vendor as Vendor);
                  cleanupApolloCache();
                  window.location.reload();
                });
              }
            },
          });
        },
      });
    }

    // Add impersonate option for admin users
    if (user?.role === EnumUserRole.Admin || impersonateUserId) {
      items.push({
        label: impersonateUserId ? 'Stop Impersonating' : 'Impersonate User',
        icon: <PermIdentity />,
        onClick: () => {
          if (impersonateUserId) {
            // Stop impersonating
            clearImpersonation();
            pushToast({
              title: 'Stopped impersonating user',
              body: 'You are now back to your admin account',
              bg: 'info',
            });
            window.location.reload();
          } else {
            // Start impersonating
            save<{ user: any }>({
              type: 'save',
              title: 'Impersonate User',
              initialValues: { user: null },
              saveText: 'Start Impersonating',
              body: <AdminUserSearch nameOverride="user" />,
              onSubmit: (values) => {
                const selectedUser = values.user;
                if (selectedUser) {
                  setImpersonateUserId(selectedUser);
                  pushToast({
                    title: 'Impersonating user',
                    body: `Switching to new user profile.`,
                    bg: 'warning',
                  });
                  window.location.reload();
                }
              },
            });
          }
        },
      });
    }
    // add on "key" prop for array rendering
    return items;
  }, [
    allVendors,
    save,
    setVendor,
    updateUserActiveVendor,
    user?._id,
    user?.role,
    vendor?._id,
    impersonateUserId,
    clearImpersonation,
    setImpersonateUserId,
    pushToast,
  ]);

  useEffect(() => {
    tagUserForGTM(user);
    setupLuckyOrange(user);
    tagUserForSentry(user);
    tagUserForIntercom(user);
  }, [user]);

  if (isLoading || isUserLoaded === false) {
    return <FullscreenLoading />;
  }

  // Producer users get the self-contained producer shell
  if (isProducer(user)) {
    return (
      <Suspense fallback={<FullscreenLoading />}>
        <ServiceWorkerProvider />
        <ProducerShell>
          <Outlet />
        </ProducerShell>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<FullscreenLoading />}>
      <ServiceWorkerProvider />
      <SiteHeader
        user={
          user
            ? {
                ...user,
                first_name: user.first_name || undefined,
              }
            : undefined
        }
        isProduction={environment.production}
        env={import.meta.env.VITE_ENV}
        title="Partners"
        navLinks={dashboardNavLinks}
        loginFn={handleLogin}
        logoutFn={async () => {
          // Mark logout to trigger cache cleanup on next login
          markLogout();
          await logout();
          navigate('/login');
        }}
        logo={
          <img
            src={!user ? whiteLogo : logo}
            height={39}
            alt={`farmshare ${!user && 'partners'} logo`}
            className="mt-1 text-body"
          />
        }
        dropdownItems={dropdownItems}
        vendorName={vendor?.shop_name}
        globalSearchElement={
          isProcessor(user) && user ? (
            <IconButton
              color="inherit"
              onClick={() => setIsSearchDrawerOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <Search />
            </IconButton>
          ) : null
        }
      />

      <Suspense fallback={null}>
        <SearchDrawer
          open={isSearchDrawerOpen}
          onClose={() => setIsSearchDrawerOpen(false)}
          onViewOnCalendar={handleSearchViewOnCalendar}
          allStatuses={ALL_SHOWN_STATUSES}
        />
      </Suspense>
      {isMobileView && isProcessorPage && user && (
        <Suspense fallback={null}>
          <FabMenu />
        </Suspense>
      )}
      <Box
        sx={{
          mx: 'auto',
          minHeight: `calc(100dvh - ${marginTop}px)`,
          width: '100%',
          pl: `${marginLeft}px !important`,
          transition: 'padding-left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
          pb: 2,
          '@media print': {
            pl: '0px !important',
            marginTop: 0,
            minHeight: 'auto',
            transition: 'none',
          },
        }}
      >
        <Outlet />
      </Box>
    </Suspense>
  );
}
